import fetch from "auth/FetchInterceptor";

export const TicketingService = {};

TicketingService.get = async ({
  page,
  pageSize,
  sort,
  outlet,
  date_from,
  date_to,
  ticket_id,
}) => {
  let apiUrl = "/v1/ticket?page=" + page + "&per_page=" + pageSize;
  if (outlet) apiUrl += "&outlet=" + outlet;
  if (date_from) apiUrl += "&date_from=" + date_from;
  if (date_to) apiUrl += "&date_to=" + date_to;
  if (ticket_id) apiUrl += "&ticket_id=" + encodeURIComponent(ticket_id);
  if (sort === "ascend") apiUrl += "&order_by=%2Dcreated_at";
  else if (sort === "descend") apiUrl += "&order_by=%2Bcreated_at";
  return fetch({
    url: apiUrl,
    method: "get",
    timeout: pageSize >= 1000 ? 120000 : undefined,
  });
};

// Fetch all tickets concurrently in batches
// First request discovers total pages, then remaining pages fire in parallel
TicketingService.getAll = async ({
  sort,
  outlet,
  date_from,
  date_to,
  ticket_id,
  batchSize = 5000,
  concurrency = 5,
  onBatchLoaded,
}) => {
  const fetchParams = { sort, outlet, date_from, date_to, ticket_id };

  // First request to determine total pages
  const firstResponse = await TicketingService.get({
    page: 1,
    pageSize: batchSize,
    ...fetchParams,
  });

  const firstBatch = firstResponse.data?.data || [];
  const total = firstResponse.data?.total || 0;
  const totalPages = firstResponse.data?.last_page || Math.ceil(total / batchSize);

  const results = new Array(totalPages).fill(null);
  results[0] = firstBatch;
  let loadedCount = firstBatch.length;

  if (onBatchLoaded) {
    onBatchLoaded({ loaded: loadedCount, total, currentPage: 1, totalPages, data: firstBatch });
  }

  if (totalPages <= 1) {
    return firstBatch;
  }

  // Fetch remaining pages concurrently
  const remainingPages = Array.from({ length: totalPages - 1 }, (_, i) => i + 2);

  const fetchPage = async (page) => {
    const response = await TicketingService.get({ page, pageSize: batchSize, ...fetchParams });
    return { page, data: response.data?.data || [] };
  };

  for (let i = 0; i < remainingPages.length; i += concurrency) {
    const chunk = remainingPages.slice(i, i + concurrency);
    const chunkResults = await Promise.all(chunk.map(fetchPage));

    for (const result of chunkResults) {
      results[result.page - 1] = result.data;
      loadedCount += result.data.length;
    }

    const allDataSoFar = results.filter(Boolean).flat();
    if (onBatchLoaded) {
      onBatchLoaded({
        loaded: loadedCount,
        total,
        currentPage: Math.min(i + concurrency + 1, totalPages),
        totalPages,
        data: allDataSoFar,
      });
    }
  }

  return results.flat();
};

TicketingService.getDetail = async (id) => {
  return fetch({
    url: "/v1/ticket/" + id,
    method: "get",
  });
};

TicketingService.getTemplate = async (id) => {
  const apiUrl = `/v1/ticket/ticket-item-delivery/template`;
  return fetch({
    url: apiUrl,
    method: "get",
    responseType: "blob",
  });
};

TicketingService.createReporting = function (data) {
  return fetch({
    url: "/v1/ticket/ticket-reporting",
    method: "post",
    data: data,
  });
};

TicketingService.createItemTransfer = function (data) {
  return fetch({
    url: "/v1/ticket/ticket-item-transfer",
    method: "post",
    data: data,
  });
};

TicketingService.createItemDelivery = function (data) {
  if (data.input_type === "bulk-input") {
    const form_data = new FormData();
    form_data.append("input_type", data.input_type);
    form_data.append("template", data.template);
    return fetch({
      url: "/v1/ticket/ticket-item-delivery",
      method: "post",
      data: form_data,
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  }
  return fetch({
    url: "/v1/ticket/ticket-item-delivery",
    method: "post",
    data: data,
  });
};

TicketingService.updateTicket = async (data) => {
  const apiUrl = `/v1/ticket/${data.url_id}`;
  return fetch({
    url: apiUrl,
    method: "put",
    data: data.details,
  });
};

TicketingService.approveTicket = async (data) => {
  const apiUrl = `/v1/ticket/${data.url_id}/approve`;
  return fetch({
    url: apiUrl,
    method: "post",
    data: data.details,
  });
};

TicketingService.rejectTicket = async (id) => {
  const apiUrl = `/v1/ticket/${id}/reject`;
  return fetch({
    url: apiUrl,
    method: "post",
  });
};

export default TicketingService;
